import { ref, computed, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useToast, POSITION } from 'vue-toastification'
import MetaService from '@/services/MetaService/MetaService'
import AccountService from '@/services/AccountService/AccountService'
import type {
  EmbeddedSignupMode,
  EmbeddedSignupSessionEvent,
  FacebookLoginResponse
} from '@/services/MetaService/types'
import type { PhoneNumber } from '@/services/AccountService/types'

type SignupStepKey = 'exchange' | 'sync' | 'register'
export type SignupStepStatus = 'pending' | 'in_progress' | 'completed' | 'skipped' | 'failed'
export type SignupFlowStatus = 'idle' | 'waiting_popup' | 'processing' | 'success' | 'error'

export interface SignupStep {
  key: SignupStepKey
  title: string
  description: string
  status: SignupStepStatus
}

interface SignupResolution {
  wabaId?: string
  phoneNumberId?: string
  phoneNumber?: PhoneNumber | null
}

interface FacebookLoginOptions {
  config_id: string
  response_type: 'code'
  override_default_response_type: boolean
  extras: {
    setup: Record<string, never>
    featureType?: string
    sessionInfoVersion?: '3'
  }
}

declare const FB: {
  login: (
    callback: (response: FacebookLoginResponse) => void,
    options: FacebookLoginOptions
  ) => void
}

function createSignupSteps (): SignupStep[] {
  return [
    {
      key: 'exchange',
      title: 'Exchange code',
      description: 'We save the business token returned by Meta.',
      status: 'pending'
    },
    {
      key: 'sync',
      title: 'Synchronize assets',
      description: 'We retrieve WABAs, phone numbers, templates, and subscriptions from Meta.',
      status: 'pending'
    },
    {
      key: 'register',
      title: 'Register phone number',
      description: 'We register the phone number with Cloud API using an automatically generated PIN.',
      status: 'pending'
    }
  ]
}

function sleep (ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms))
}

function buildPhonePin (): string {
  return String(Math.floor(100000 + (Math.random() * 900000)))
}

function getErrorMessage (error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message
  }
  return 'An unexpected error occurred.'
}

function isValidFacebookOrigin (origin: string): boolean {
  try {
    const url = new URL(origin)
    return url.hostname === 'facebook.com' || url.hostname.endsWith('.facebook.com')
  } catch {
    return false
  }
}

function parseEmbeddedSignupEvent (payload: unknown): EmbeddedSignupSessionEvent | null {
  try {
    const parsedPayload = typeof payload === 'string'
      ? JSON.parse(payload)
      : payload

    if (
      parsedPayload &&
      typeof parsedPayload === 'object' &&
      'type' in parsedPayload &&
      parsedPayload.type === 'WA_EMBEDDED_SIGNUP'
    ) {
      return parsedPayload as EmbeddedSignupSessionEvent
    }
  } catch {
    return null
  }

  return null
}

export interface UseEmbeddedSignupOptions {
  onSuccess?: () => Promise<void> | void
}

export function useEmbeddedSignup (options: UseEmbeddedSignupOptions = {}) {
  const router = useRouter()
  const toast = useToast()
  const facebookConfigId = process.env.VUE_APP_FACEBOOK_CONFIG_ID

  const signupMode = ref<EmbeddedSignupMode>('cloud_api')
  const signupFlowStatus = ref<SignupFlowStatus>('idle')
  const signupErrorMessage = ref('')
  const signupSteps = ref<SignupStep[]>(createSignupSteps())
  const embeddedSignupEvent = ref<EmbeddedSignupSessionEvent | null>(null)

  const isSignupActionDisabled = computed(() => {
    return signupFlowStatus.value === 'waiting_popup' || signupFlowStatus.value === 'processing'
  })

  const showSignupProgressModal = computed(() => {
    return signupFlowStatus.value !== 'idle'
  })

  const signupModalTitle = computed(() => {
    if (signupFlowStatus.value === 'waiting_popup') {
      return signupMode.value === 'coexistence'
        ? 'Complete the connection in Meta'
        : 'Complete the phone number setup in Meta'
    }
    if (signupFlowStatus.value === 'processing') return 'Finalizing onboarding'
    if (signupFlowStatus.value === 'success') return 'Onboarding complete'
    return "We couldn't complete onboarding"
  })

  const signupModalDescription = computed(() => {
    if (signupFlowStatus.value === 'waiting_popup') {
      return signupMode.value === 'coexistence'
        ? "Follow the Coexistence flow in the Meta popup. When it finishes, we'll complete the connection automatically."
        : "Follow the Meta flow to register or migrate the phone number. When it finishes, we'll complete onboarding automatically."
    }
    if (signupFlowStatus.value === 'processing') {
      return "We're exchanging the code, synchronizing assets, and setting up the phone number."
    }
    if (signupFlowStatus.value === 'success') {
      return "The account is connected. We'll take you to the corresponding asset."
    }
    return 'Review the error details and try again.'
  })

  function resetSignupState (mode: EmbeddedSignupMode): void {
    signupMode.value = mode
    signupFlowStatus.value = 'waiting_popup'
    signupErrorMessage.value = ''
    signupSteps.value = createSignupSteps()
    embeddedSignupEvent.value = null
  }

  function closeSignupModal (): void {
    if (signupFlowStatus.value === 'waiting_popup' || signupFlowStatus.value === 'processing') {
      return
    }
    signupFlowStatus.value = 'idle'
    signupErrorMessage.value = ''
    embeddedSignupEvent.value = null
  }

  function updateSignupStep (key: SignupStepKey, status: SignupStepStatus, description?: string): void {
    signupSteps.value = signupSteps.value.map(step => {
      if (step.key !== key) return step
      return { ...step, status, description: description ?? step.description }
    })
  }

  function toastError (message: string): void {
    try {
      toast.error(String(message), { position: POSITION.BOTTOM_LEFT })
    } catch (error) {
      console.log('error on toast:', error)
    }
  }

  function toastSuccess (message: string): void {
    try {
      toast.success(String(message), { position: POSITION.BOTTOM_LEFT })
    } catch (error) {
      console.log('error on toast:', error)
    }
  }

  function handleSignupFailure (message: string): void {
    signupSteps.value = signupSteps.value.map(step =>
      step.status === 'in_progress' ? { ...step, status: 'failed' as const } : step
    )
    signupFlowStatus.value = 'error'
    signupErrorMessage.value = message
    toastError(message)
  }

  function getSignupFailureMessage (sessionEvent: EmbeddedSignupSessionEvent): string {
    if (sessionEvent.event === 'CANCEL') {
      if (sessionEvent.data.error_message) return sessionEvent.data.error_message
      if (sessionEvent.data.current_step) return `The flow was canceled at ${sessionEvent.data.current_step}.`
      return 'The flow was canceled before completion.'
    }
    return sessionEvent.data.error_message ?? 'Meta reported an error during Embedded Signup.'
  }

  function handleEmbeddedSignupMessage (event: MessageEvent): void {
    if (!isValidFacebookOrigin(event.origin)) return
    const sessionEvent = parseEmbeddedSignupEvent(event.data)
    if (!sessionEvent) return
    embeddedSignupEvent.value = sessionEvent
  }

  async function waitForEmbeddedSignupEvent (timeoutMs: number = 8000): Promise<EmbeddedSignupSessionEvent | null> {
    const startedAt = Date.now()
    while (Date.now() - startedAt < timeoutMs) {
      if (embeddedSignupEvent.value) return embeddedSignupEvent.value
      await sleep(200)
    }
    return embeddedSignupEvent.value
  }

  function buildSignupOptions (mode: EmbeddedSignupMode): FacebookLoginOptions {
    const extras: FacebookLoginOptions['extras'] = {
      setup: {},
      sessionInfoVersion: '3'
    }
    if (mode === 'coexistence') {
      extras.featureType = 'whatsapp_business_app_onboarding'
    }
    return {
      config_id: facebookConfigId ?? '',
      response_type: 'code',
      override_default_response_type: true,
      extras
    }
  }

  async function resolveSignupAssets (
    sessionEvent: EmbeddedSignupSessionEvent | null
  ): Promise<SignupResolution> {
    const wabaId = sessionEvent?.data.waba_id
    const phoneNumberId = sessionEvent?.data.phone_number_id
    if (!wabaId) return {}
    if (!phoneNumberId) return { wabaId }

    const accountService = await AccountService.getInstance()
    const accountPhoneNumbers = await accountService.getWhatsappBusinessAccountPhoneNumbers(wabaId)
    const phoneNumber = accountPhoneNumbers.find(phone => phone.id === phoneNumberId)
    if (!phoneNumber) return { wabaId, phoneNumberId, phoneNumber: null }

    return {
      wabaId,
      phoneNumberId,
      phoneNumber: { ...phoneNumber, waba_id: phoneNumber.waba_id ?? wabaId }
    }
  }

  async function navigateAfterSignup (signupResolution: SignupResolution): Promise<void> {
    if (signupResolution.wabaId && signupResolution.phoneNumberId) {
      await router.push({
        name: 'phone-number',
        params: { wabaId: signupResolution.wabaId, phoneNumberId: signupResolution.phoneNumberId }
      })
      return
    }
    if (signupResolution.wabaId) {
      await router.push({ name: 'whatsapp-business-account', params: { wabaId: signupResolution.wabaId } })
      return
    }
    await router.push({ name: 'whatsapp-business-accounts' })
  }

  async function finalizeEmbeddedSignup (code: string): Promise<void> {
    signupFlowStatus.value = 'processing'

    const metaService = await MetaService.getInstance()
    const sessionEvent = await waitForEmbeddedSignupEvent()

    if (sessionEvent?.event === 'CANCEL' || sessionEvent?.event === 'ERROR') {
      handleSignupFailure(getSignupFailureMessage(sessionEvent))
      return
    }

    let signupResolution: SignupResolution = {}

    try {
      updateSignupStep('exchange', 'in_progress')
      await metaService.exchangeCodeForToken(code)
      updateSignupStep('exchange', 'completed')

      updateSignupStep('sync', 'in_progress')
      await metaService.synchronizeWithMeta()
      updateSignupStep('sync', 'completed')

      signupResolution = await resolveSignupAssets(sessionEvent)

      if (
        signupMode.value === 'cloud_api' &&
        signupResolution.wabaId &&
        signupResolution.phoneNumberId &&
        (sessionEvent?.event === 'FINISH' || sessionEvent?.event === 'FINISH_OBO_MIGRATION')
      ) {
        updateSignupStep('register', 'in_progress')
        try {
          await metaService.registerPhoneNumberById(
            signupResolution.wabaId,
            signupResolution.phoneNumberId,
            buildPhonePin()
          )
          updateSignupStep('register', 'completed')
        } catch (registerError) {
          const msg = getErrorMessage(registerError)
          const isAlreadyRegistered = msg.includes('133005') || msg.includes('PIN Mismatch')
          if (isAlreadyRegistered) {
            updateSignupStep('register', 'skipped', 'The phone number is already registered with a previous PIN.')
          } else {
            throw registerError
          }
        }
      } else if (signupMode.value === 'coexistence') {
        updateSignupStep('register', 'skipped', "Coexistence doesn't require registering the phone number again.")
      } else {
        updateSignupStep('register', 'skipped', "Embedded Signup didn't return a phone number that can be registered.")
      }

      signupFlowStatus.value = 'success'
      toastSuccess(
        signupMode.value === 'coexistence'
          ? 'WhatsApp Business App connected successfully'
          : 'WhatsApp connected successfully'
      )

      if (options.onSuccess) {
        await options.onSuccess()
      }

      await sleep(800)
      closeSignupModal()
    } catch (error) {
      handleSignupFailure(`Error connecting WhatsApp: ${getErrorMessage(error)}`)
      return
    }

    await navigateAfterSignup(signupResolution)
  }

  async function handleFacebookLoginResponse (response: FacebookLoginResponse): Promise<void> {
    if (!response.authResponse?.code) {
      const sessionEvent = await waitForEmbeddedSignupEvent(1000)
      if (sessionEvent?.event === 'CANCEL' || sessionEvent?.event === 'ERROR') {
        handleSignupFailure(getSignupFailureMessage(sessionEvent))
        return
      }
      signupFlowStatus.value = 'idle'
      toastError('Error connecting WhatsApp: the flow was canceled or not authorized.')
      return
    }
    await finalizeEmbeddedSignup(response.authResponse.code)
  }

  function launchWhatsAppSignup (mode: EmbeddedSignupMode): void {
    if (!facebookConfigId) {
      toastError('Facebook Login configuration is missing for Embedded Signup.')
      return
    }
    if (typeof FB === 'undefined') {
      toastError('The Facebook SDK is not available yet. Try again in a few seconds.')
      return
    }
    resetSignupState(mode)
    FB.login((response: FacebookLoginResponse) => {
      handleFacebookLoginResponse(response).catch(error => {
        handleSignupFailure(`Error connecting WhatsApp: ${getErrorMessage(error)}`)
      })
    }, buildSignupOptions(mode))
  }

  window.addEventListener('message', handleEmbeddedSignupMessage)
  onBeforeUnmount(() => {
    window.removeEventListener('message', handleEmbeddedSignupMessage)
  })

  return {
    signupMode,
    signupFlowStatus,
    signupErrorMessage,
    signupSteps,
    isSignupActionDisabled,
    showSignupProgressModal,
    signupModalTitle,
    signupModalDescription,
    launchWhatsAppSignup,
    closeSignupModal
  }
}
